import { Link, type Href } from 'expo-router';
import { CheckCircle2Icon, XCircleIcon } from 'lucide-react-native';
import { Pressable, View } from 'react-native';

import { Card, CardContent, CardHeader, CardTitle } from '@sixam/ui/card';
import { Icon } from '@sixam/ui/icon';
import { Text } from '@sixam/ui/text';
import type { EventAttendanceStatus } from '@/lib/event-attendance';
import { formatEventWhen, isEventFull, spotsLeft, type Event } from '@/lib/events';

type EventSummaryProps = Readonly<{
  event: Event;
  attendanceStatus?: EventAttendanceStatus;
  clubName?: string;
  href?: Href;
  timeZone?: string;
  showStatus?: boolean;
}>;

function StatusBadge({ status }: Readonly<{ status: Event['status'] }>) {
  const label = status === 'draft' ? 'Draft' : 'Cancelled';
  const tone =
    status === 'cancelled'
      ? 'bg-destructive/15 text-destructive'
      : 'bg-muted text-muted-foreground';
  return (
    <View className={`rounded-full px-2 py-0.5 ${tone}`}>
      <Text className="text-xs font-medium">{label}</Text>
    </View>
  );
}

function AttendanceLine({ clubName, event }: Readonly<{ clubName?: string; event: Event }>) {
  const left = spotsLeft(event);
  const full = isEventFull(event);
  const prefix = clubName ? `${clubName} · ` : '';

  if (event.capacity == null) {
    return (
      <Text variant="muted">
        {prefix}
        {event.attendee_count} going
      </Text>
    );
  }

  return (
    <Text variant="muted">
      {prefix}
      {event.attendee_count}/{event.capacity} going{full ? ' · Full' : ` · ${left} left`}
    </Text>
  );
}

function EventSummaryContent({
  attendanceStatus = 'none',
  clubName,
  event,
  showStatus,
  timeZone,
}: Omit<EventSummaryProps, 'href'>) {
  const activeAttendance = attendanceStatus === 'registered' || attendanceStatus === 'pending';
  const cancelledAttendance = attendanceStatus === 'cancelled';

  return (
    <Card className={activeAttendance ? 'border-primary bg-primary/10' : undefined}>
      <CardHeader className="gap-1">
        <View className="flex-row items-start justify-between gap-2">
          <CardTitle className="flex-1">{event.title}</CardTitle>
          {activeAttendance ? (
            <View className="flex-row items-center gap-1 rounded-full bg-primary px-2 py-1">
              <CheckCircle2Icon color="white" size={13} />
              <Text className="font-sans text-[10px] font-bold uppercase tracking-widest text-white">
                {attendanceStatus === 'pending' ? 'Payment pending' : 'Registered'}
              </Text>
            </View>
          ) : null}
          {cancelledAttendance ? (
            <View className="flex-row items-center gap-1 rounded-full bg-destructive/15 px-2 py-1">
              <Icon as={XCircleIcon} className="size-3.5 text-destructive" />
              <Text className="font-sans text-[10px] font-bold uppercase tracking-widest text-destructive">
                Cancelled
              </Text>
            </View>
          ) : null}
          {showStatus && event.status !== 'published' ? (
            <StatusBadge status={event.status} />
          ) : null}
        </View>
        <Text variant="muted">{formatEventWhen(event.starts_at, event.ends_at, timeZone)}</Text>
      </CardHeader>
      <CardContent className="gap-1">
        {activeAttendance ? (
          <Text className="font-medium text-primary">
            {attendanceStatus === 'pending'
              ? 'You are registered. Complete payment to lock the spot.'
              : 'You are registered!'}
          </Text>
        ) : null}
        {cancelledAttendance ? (
          <Text className="font-medium text-destructive">Your attendance is cancelled.</Text>
        ) : null}
        <Text variant="small">{event.place_name}</Text>
        <AttendanceLine clubName={clubName} event={event} />
      </CardContent>
    </Card>
  );
}

export function EventSummary({ href, ...summaryProps }: EventSummaryProps) {
  const content = <EventSummaryContent {...summaryProps} />;

  if (!href) {
    return content;
  }

  return (
    <Link href={href} asChild>
      <Pressable className="active:opacity-80">{content}</Pressable>
    </Link>
  );
}
